import React, { useState } from "react";
import { X, Loader2, Upload } from "lucide-react";
import { api, absUrl } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { useSettings } from "../../context/SettingsContext";

const FIELDS = [
  { key: "brandName", label: "BRAND NAME" },
  { key: "tagline", label: "TAGLINE (marquee)" },
  { key: "whatsapp", label: "WHATSAPP (intl, e.g. 972538256594)" },
  { key: "whatsappDisplay", label: "PHONE DISPLAY" },
  { key: "email", label: "EMAIL" },
  { key: "instagram", label: "INSTAGRAM URL" },
  { key: "shippingText", label: "SHIPPING BANNER TEXT" },
  { key: "heroVideo", label: "HERO VIDEO URL (.mp4 - leave empty to use image)" },
  { key: "editorialText", label: 'BANNER TEXT (the "DESIGNED TO BE LOVED" banner)' },
];

export default function SettingsForm({ onClose }) {
  const { settings, refresh } = useSettings();
  const { toast } = useToast();
  const [form, setForm] = useState({ ...settings });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleEditorialUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await new Promise((res) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.readAsDataURL(file);
      });
      const { url } = await api.uploadImage(dataUrl);
      set("editorialImage", url);
      toast({ title: "Uploaded", description: "Banner image updated." });
    } catch {
      toast({ title: "Upload failed", description: "Please try a smaller image." });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleInstagramUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const current = Array.isArray(form.instagramImages) ? [...form.instagramImages] : [];
      for (const file of files) {
        const dataUrl = await new Promise((res) => {
          const r = new FileReader();
          r.onload = () => res(r.result);
          r.readAsDataURL(file);
        });
        const { url } = await api.uploadImage(dataUrl);
        current.push(url);
      }
      set("instagramImages", current);
      toast({ title: "Uploaded", description: "Instagram image(s) added." });
    } catch {
      toast({ title: "Upload failed", description: "Please try a smaller image." });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeInstagram = (i) =>
    set("instagramImages", (form.instagramImages || []).filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.updateSettings(form);
      await refresh();
      toast({ title: "Saved", description: "Store settings updated." });
      onClose();
    } catch (err) {
      toast({ title: "Error", description: "Could not save settings." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start md:items-center justify-center overflow-y-auto p-4">
      <form onSubmit={submit} className="w-full max-w-lg bg-white my-8">
        <div className="flex items-center justify-between border-b border-neutral-200 px-6 py-4 sticky top-0 bg-white z-10">
          <h2 className="font-display text-xl tracking-wide">Store settings</h2>
          <button type="button" onClick={onClose} className="text-neutral-500 hover:text-black">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-6 py-6 space-y-4">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <label className="block text-xs tracking-widest text-neutral-500 mb-1">{f.label}</label>
              <input value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} className="w-full border border-neutral-300 px-3 py-2.5 text-sm outline-none focus:border-black" />
            </div>
          ))}

          {/* Editorial banner image upload */}
          <div>
            <label className="block text-xs tracking-widest text-neutral-500 mb-2">BANNER IMAGE (the big "DESIGNED TO BE LOVED" banner)</label>
            <div className="flex items-center gap-3">
              <div className="h-24 w-32 bg-neutral-100 overflow-hidden border border-neutral-200 shrink-0">
                {form.editorialImage ? (
                  <img src={absUrl(form.editorialImage)} alt="banner" className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-[10px] text-neutral-400 text-center px-2">
                    אוטומטי (תמונת מוצר)
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <label className="inline-flex items-center gap-2 cursor-pointer text-xs tracking-[0.15em] border border-neutral-300 px-4 py-2.5 rounded-md hover:border-black transition-colors">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {uploading ? "..." : "UPLOAD IMAGE"}
                  <input type="file" accept="image/*" onChange={handleEditorialUpload} className="hidden" disabled={uploading} />
                </label>
                {form.editorialImage && (
                  <button type="button" onClick={() => set("editorialImage", "")} className="text-xs text-neutral-500 hover:text-red-600 text-right">
                    הסר תמונה (חזרה לאוטומטי)
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Instagram section images */}
          <div>
            <label className="block text-xs tracking-widest text-neutral-500 mb-2">INSTAGRAM IMAGES (the "VISIT OUR INSTAGRAM" section)</label>
            <div className="flex flex-wrap gap-3">
              {(form.instagramImages || []).map((img, i) => (
                <div key={i} className="relative h-24 w-24 bg-neutral-100 overflow-hidden border border-neutral-200 rounded-md">
                  <img src={absUrl(img)} alt="" className="h-full w-full object-cover" />
                  <button type="button" onClick={() => removeInstagram(i)} className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-0.5" title="הסר">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              <label className="h-24 w-24 border border-dashed border-neutral-300 rounded-md flex flex-col items-center justify-center cursor-pointer text-neutral-400 hover:border-black hover:text-black transition-colors">
                {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
                <span className="text-[10px] mt-1">{uploading ? "..." : "UPLOAD"}</span>
                <input type="file" accept="image/*" multiple onChange={handleInstagramUpload} className="hidden" disabled={uploading} />
              </label>
            </div>
            <p className="mt-2 text-[11px] text-neutral-400">מומלץ 4 תמונות. אם לא תעלה — יוצגו תמונות מוצרים אוטומטית.</p>
          </div>
        </div>
        <div className="border-t border-neutral-200 px-6 py-4 flex justify-end gap-3 sticky bottom-0 bg-white">
          <button type="button" onClick={onClose} className="px-5 py-2.5 text-xs tracking-[0.2em] border border-neutral-300 hover:border-black">CANCEL</button>
          <button type="submit" disabled={saving || uploading} className="px-6 py-2.5 text-xs tracking-[0.2em] bg-black text-white hover:bg-neutral-800 disabled:opacity-50 flex items-center gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} SAVE
          </button>
        </div>
      </form>
    </div>
  );
}
